import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  SxProps,
  Theme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';

interface AccordionCardProps {
  /** Contenido del header del acordeón */
  summary: React.ReactNode;
  /** Contenido expandible del acordeón */
  children: React.ReactNode;
  /** Si el acordeón está expandido */
  expanded?: boolean;
  /** Callback cuando cambia el estado de expansión */
  onChange?: (event: React.SyntheticEvent, isExpanded: boolean) => void;
  /** Tamaño del padding (default: 'medium') */
  size?: 'small' | 'medium' | 'large';
  /** Habilitar efectos hover (default: true) */
  enabledEffects?: boolean;
  /** Deshabilitar el acordeón */
  disabled?: boolean;
  /** Estilos personalizados */
  sx?: SxProps<Theme>;
  /** Clase CSS personalizada */
  className?: string;
  /** Mostrar icono de expansión (default: true) */
  showExpandIcon?: boolean;
  /** ID único para el panel */
  id?: string;
}

const StyledAccordion = styled(Accordion)<{ enabledEffects?: boolean }>(
  ({ enabledEffects, theme }) => ({
    background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
    borderRadius: '16px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #D7D7D7',
    transition: 'all 0.3s ease-in-out',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
    },
    ...(enabledEffects && {
      '&:hover': {
        boxShadow: '0 6px 20px rgba(15, 35, 223, 0.15)',
        transform: 'translateY(-2px)',
      },
    }),
    '& .MuiAccordionSummary-root': {
      borderRadius: '16px',
      padding: 0,
      minHeight: 'auto',
      '&.Mui-expanded': {
        minHeight: 'auto',
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
    },
    '& .MuiAccordionSummary-content': {
      margin: 0,
      '&.Mui-expanded': {
        margin: 0,
      },
    },
    '& .MuiAccordionDetails-root': {
      padding: 0,
      borderRadius: '0 0 16px 16px',
    },
  }),
);

const AccordionCard: React.FC<AccordionCardProps> = ({
  summary,
  children,
  expanded,
  onChange,
  size = 'medium',
  enabledEffects = true,
  disabled = false,
  sx,
  className,
  showExpandIcon = true,
  id,
}) => {
  const summaryPadding = useMemo(() => {
    if (size === 'small') return '12px';
    if (size === 'medium') return '16px 24px';
    return '20px 32px';
  }, [size]);

  const detailsPadding = useMemo(() => {
    if (size === 'small') return '0 12px 12px 12px';
    if (size === 'medium') return '0 24px 24px 24px';
    return '0 32px 32px 32px';
  }, [size]);

  return (
    <StyledAccordion
      expanded={expanded}
      onChange={onChange}
      disabled={disabled}
      enabledEffects={enabledEffects}
      sx={sx}
      className={className}
      id={id}
    >
      <AccordionSummary
        expandIcon={
          showExpandIcon ? (
            <ExpandMore
              sx={{
                color: 'text.secondary',
                transition: 'transform 0.3s ease',
              }}
            />
          ) : undefined
        }
        sx={{
          padding: summaryPadding,
        }}
      >
        <Box sx={{ width: '100%' }}>{summary}</Box>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: detailsPadding }}>
        {children}
      </AccordionDetails>
    </StyledAccordion>
  );
};

export default AccordionCard;
